import { Intent } from '@blueprintjs/core';
import omit from 'lodash.omit';
import { TrackingEventList } from 'portal-sdk';
import { sendHubspotEvent } from 'vet-bones/utils/portal';

import type { ProjectsAction } from 'src/actions/projects/actionCreators';
import { ProjectsActionType } from 'src/actions/projects/actionTypes';
import {
  DEFAULT_PROJECT_PROPERTY_TYPE,
  ProjectPropertyType,
} from 'src/constants/project';
import { ModelRule } from 'src/containers/ProjectDiagram/types/data';
import { createEmptyDiagramSnapshot } from 'src/containers/ProjectDiagram/types/diagram';
import {
  getDefaultProjectDataPrefix,
  getDefaultProjectModelPrefix,
} from 'src/containers/ProjectDiagram/utils/generateProjectIri';
import {
  Project,
  ProjectModel,
  ProjectsState,
  createEmptyPublishHistory,
} from 'src/store/state/projects';
import { parseModelClassResults } from 'src/utils/binding/parseModelClassResults';
import { parseModelPropertyResults } from 'src/utils/binding/parseModelPropertyResults';
import { parseModelRuleResults } from 'src/utils/binding/parseModelRuleResults';
import { duplicateProject } from 'src/utils/duplicate/duplicateProject';
import { parseMetatdataResults } from 'src/utils/jsonld/parseMetatdataResults';

export const projectsReducer = (
  prevState: ProjectsState = new ProjectsState(),
  action: ProjectsAction
): ProjectsState => {
  switch (action.type) {
    case ProjectsActionType.ADDED_PROJECT: {
      const { id, dateCreated = Date.now(), ...projectData } = action.payload;
      const project: Project = {
        id,
        ...projectData,
        dateCreated,
        dateLastUpdated: dateCreated,
        isRemoved: false,
        diagram: createEmptyDiagramSnapshot(),
        fileUploadsById: {},
        virtualGraphsById: {},
        modelsById: {},
        modelNamespace: '',
        dataNamespace: '',
        propertyType: DEFAULT_PROJECT_PROPERTY_TYPE,
        publishHistory: createEmptyPublishHistory(),
      };
      project.modelNamespace = getDefaultProjectModelPrefix(project);
      project.dataNamespace = getDefaultProjectDataPrefix(project);
      const projectIds = [...prevState.projectIds, project.id];
      const projectsById = {
        ...prevState.projectsById,
        [project.id]: project,
      };
      return {
        ...prevState,
        projectIds,
        projectsById,
      };
    }

    case ProjectsActionType.ADDED_FILE_UPLOAD_TO_PROJECT: {
      const { id, fileUpload } = action.payload;

      const prevProject = prevState.projectsById[id];
      if (!prevProject) {
        return prevState;
      }

      const dateLastUpdated = Date.now();

      return {
        ...prevState,
        projectsById: {
          ...prevState.projectsById,
          [id]: {
            ...prevProject,
            fileUploadsById: {
              ...prevProject.fileUploadsById,
              [fileUpload.id]: {
                ...fileUpload,
                id: fileUpload.id,
                dateLastUpdated,
              },
            },
            dateLastUpdated,
          },
        },
      };
    }

    case ProjectsActionType.ADDED_VIRTUAL_GRAPHS_TO_PROJECT: {
      const { id, virtualGraphs } = action.payload;

      const prevProject = prevState.projectsById[id];
      if (!prevProject) {
        return prevState;
      }

      const dateLastUpdated = Date.now();

      const virtualGraphsById = {
        ...prevProject.virtualGraphsById,
      };
      virtualGraphs.forEach((virtualGraph) => {
        virtualGraphsById[virtualGraph.id] = {
          ...virtualGraph,
          id: virtualGraph.id,
          dateLastUpdated,
        };
      });

      return {
        ...prevState,
        projectsById: {
          ...prevState.projectsById,
          [id]: {
            ...prevProject,
            virtualGraphsById,
            dateLastUpdated,
          },
        },
      };
    }

    case ProjectsActionType.UPDATED_PROJECT_VIRTUAL_GRAPH_PREVIEW: {
      const { projectId, resourceId, fields, previewRows, connectionId } =
        action.payload;

      const prevProject = prevState.projectsById[projectId];
      if (!prevProject) {
        return prevState;
      }

      const dateLastUpdated = Date.now();

      return {
        ...prevState,
        projectsById: {
          ...prevState.projectsById,
          [projectId]: {
            ...prevProject,
            virtualGraphsById: {
              ...prevProject.virtualGraphsById,
              [resourceId]: {
                ...prevProject.virtualGraphsById[resourceId],
                connectionId,
                dateLastUpdated,
                fields,
                previewRows,
              },
            },
            dateLastUpdated,
          },
        },
      };
    }

    case ProjectsActionType.DUPLICATED_PROJECT: {
      const { id, copyId } = action.payload;

      const project = prevState.projectsById[id];
      if (!project) {
        return prevState;
      }

      const newProject = duplicateProject(project, {
        id: copyId,
        name: `${project.name} Copy`,
        dataNamespace: project.dataNamespace,
        modelNamespace: project.modelNamespace,
      });

      return {
        ...prevState,
        projectIds: [...prevState.projectIds, copyId],
        projectsById: {
          ...prevState.projectsById,
          [copyId]: newProject,
        },
      };
    }

    case ProjectsActionType.IMPORTED_PROJECT_FROM_FILE: {
      const { project } = action.payload;

      return {
        ...prevState,
        projectIds: [...prevState.projectIds, project.id],
        projectsById: {
          ...prevState.projectsById,
          [project.id]: project,
        },
      };
    }

    case ProjectsActionType.REMOVED_PROJECT: {
      // Just add the isRemoved flag; projectIds and projectsById
      // are updated later in /src/store/prune/prune.ts
      const { id } = action.payload;
      const project = {
        ...prevState.projectsById[id],
        isRemoved: true,
      };
      const projectsById = {
        ...prevState.projectsById,
        [project.id]: project,
      };

      return {
        ...prevState,
        projectsById,
      };
    }

    case ProjectsActionType.RENAMED_PROJECT: {
      const { id, name } = action.payload;
      const prevProject = prevState.projectsById[id];
      if (!prevProject) {
        return prevState;
      }

      const project = {
        ...prevProject,
        name,
      };
      const projectsById = {
        ...prevState.projectsById,
        [project.id]: project,
      };

      return {
        ...prevState,
        projectsById,
      };
    }

    case ProjectsActionType.RESTORED_PROJECT: {
      const { id } = action.payload;
      const project = {
        ...prevState.projectsById[id],
        isRemoved: false,
      };
      const projectsById = {
        ...prevState.projectsById,
        [project.id]: project,
      };

      return {
        ...prevState,
        projectsById,
      };
    }

    case ProjectsActionType.UPDATED_PROJECT_DIAGRAM: {
      const { id, diagram } = action.payload;
      const dateLastUpdated = Date.now();

      const prevProject = prevState.projectsById[id];
      if (!prevProject) {
        return prevState;
      }

      return {
        ...prevState,
        projectsById: {
          ...prevState.projectsById,
          [id]: {
            ...prevProject,
            dateLastUpdated,
            diagram,
          },
        },
      };
    }

    case ProjectsActionType.UPDATED_PROJECT_MODEL_NAMESPACE: {
      const { id, modelNamespace } = action.payload;
      const dateLastUpdated = Date.now();

      const prevProject = prevState.projectsById[id];
      if (!prevProject) {
        return prevState;
      }

      return {
        ...prevState,
        projectsById: {
          ...prevState.projectsById,
          [id]: {
            ...prevProject,
            dateLastUpdated,
            modelNamespace,
          },
        },
      };
    }

    case ProjectsActionType.UPDATED_PROJECT_DATA_NAMESPACE: {
      const { id, dataNamespace } = action.payload;
      const dateLastUpdated = Date.now();

      const prevProject = prevState.projectsById[id];
      if (!prevProject) {
        return prevState;
      }

      return {
        ...prevState,
        projectsById: {
          ...prevState.projectsById,
          [id]: {
            ...prevProject,
            dateLastUpdated,
            dataNamespace,
          },
        },
      };
    }

    case ProjectsActionType.UPDATED_PROJECT_PROPERTY_TYPE: {
      const { projectId, propertyType } = action.payload;
      const prevProject = prevState.projectsById[projectId];
      if (!prevProject) {
        return prevState;
      }

      return {
        ...prevState,
        projectsById: {
          ...prevState.projectsById,
          [projectId]: {
            ...prevProject,
            dateLastUpdated: Date.now(),
            propertyType,
          },
        },
      };
    }

    case ProjectsActionType.ADD_MODELS_TO_PROJECT_STARTED: {
      const { id, modelNamesAndIds, ...model } = action.payload;

      const prevProject = prevState.projectsById[id];
      if (!prevProject) {
        return prevState;
      }

      const dateLastUpdated = Date.now();
      const modelsById = modelNamesAndIds.reduce<Record<string, ProjectModel>>(
        (acc, { modelName, modelId }) => {
          acc[modelId] = {
            ...model,
            id: modelId,
            name: modelName,
            dateLastUpdated,
            classes: null,
            properties: null,
            colorIdsByIri: null,
            descriptionsByIri: null,
            labelsByIri: null,
            rules: null,
          };
          return acc;
        },
        { ...prevProject.modelsById }
      );

      return {
        ...prevState,
        projectsById: {
          ...prevState.projectsById,
          [id]: {
            ...prevProject,
            dateLastUpdated,
            modelsById,
          },
        },
      };
    }

    case ProjectsActionType.REQUEST_PROJECT_MODEL_CLASSES_SUCCESS: {
      const { id, modelId } = action.payload.action;
      const results = action.payload.response?.body?.results?.bindings || [];

      const prevProject = prevState.projectsById[id];
      if (!prevProject) {
        return prevState;
      }

      const prevModel = prevProject.modelsById[modelId];
      if (!prevModel) {
        return prevState;
      }

      const classes = parseModelClassResults(results);

      return {
        ...prevState,
        projectsById: {
          ...prevState.projectsById,
          [id]: {
            ...prevProject,
            modelsById: {
              ...prevProject.modelsById,
              [modelId]: {
                ...prevModel,
                classes,
              },
            },
          },
        },
      };
    }

    case ProjectsActionType.REQUEST_PROJECT_MODEL_PROPERTIES_SUCCESS: {
      const { id, modelId } = action.payload.action;
      const results = action.payload.response?.body?.results?.bindings || [];

      const prevProject = prevState.projectsById[id];
      if (!prevProject) {
        return prevState;
      }

      const prevModel = prevProject.modelsById[modelId];
      if (!prevModel) {
        return prevState;
      }

      const properties = parseModelPropertyResults(results);

      // change project property type to CUSTOM if any properties contradict current setting
      const propertyType =
        prevProject.propertyType !== ProjectPropertyType.CUSTOM &&
        Object.values(properties).some(
          (property) => property.propertyType !== prevProject.propertyType
        )
          ? ProjectPropertyType.CUSTOM
          : prevProject.propertyType;

      return {
        ...prevState,
        projectsById: {
          ...prevState.projectsById,
          [id]: {
            ...prevProject,
            propertyType,
            modelsById: {
              ...prevProject.modelsById,
              [modelId]: {
                ...prevModel,
                properties,
              },
            },
          },
        },
      };
    }

    case ProjectsActionType.REQUEST_PROJECT_MODEL_LABELS_AND_DESCRIPTIONS_SUCCESS: {
      const { id, modelId } = action.payload.action;
      const results = action.payload.response?.body || [];

      const prevProject = prevState.projectsById[id];
      if (!prevProject) {
        return prevState;
      }

      const prevModel = prevProject.modelsById[modelId];
      if (!prevModel) {
        return prevState;
      }

      const { colorIdsByIri, descriptionsByIri, labelsByIri } =
        parseMetatdataResults(results);

      return {
        ...prevState,
        projectsById: {
          ...prevState.projectsById,
          [id]: {
            ...prevProject,
            modelsById: {
              ...prevProject.modelsById,
              [modelId]: {
                ...prevModel,
                colorIdsByIri: {
                  ...prevModel.colorIdsByIri,
                  ...colorIdsByIri,
                },
                descriptionsByIri: {
                  ...prevModel.descriptionsByIri,
                  ...descriptionsByIri,
                },
                labelsByIri: {
                  ...prevModel.labelsByIri,
                  ...labelsByIri,
                },
              },
            },
          },
        },
      };
    }

    case ProjectsActionType.REQUEST_PROJECT_MODEL_RULES_SUCCESS: {
      const { id, modelId } = action.payload.action;
      const results = action.payload.response?.body?.results?.bindings || [];

      const rules: Record<string, ModelRule> = parseModelRuleResults(results);

      const prevProject = prevState.projectsById[id];
      if (!prevProject) {
        return prevState;
      }

      const prevModel = prevProject.modelsById[modelId];
      if (!prevModel) {
        return prevState;
      }

      return {
        ...prevState,
        projectsById: {
          ...prevState.projectsById,
          [id]: {
            ...prevProject,
            modelsById: {
              ...prevProject.modelsById,
              [modelId]: {
                ...prevModel,
                rules,
              },
            },
          },
        },
      };
    }

    case ProjectsActionType.ADD_VOICEBOX_MODEL_TO_PROJECT_SUCCESS: {
      const { id, projectModel } = action.payload;

      const prevProject = prevState.projectsById[id];
      if (!prevProject) {
        return prevState;
      }

      // change project property type to CUSTOM if any properties contradict current setting
      const propertyType =
        projectModel.properties &&
        prevProject.propertyType !== ProjectPropertyType.CUSTOM &&
        Object.values(projectModel.properties).some(
          (property) => property.propertyType !== prevProject.propertyType
        )
          ? ProjectPropertyType.CUSTOM
          : prevProject.propertyType;

      return {
        ...prevState,
        projectsById: {
          ...prevState.projectsById,
          [id]: {
            ...prevProject,
            propertyType,
            modelsById: {
              ...prevProject.modelsById,
              [projectModel.id]: projectModel,
            },
          },
        },
      };
    }

    case ProjectsActionType.ACCEPTED_PROJECT_DRAFT_DIAGRAM: {
      const { id, diagram } = action.payload;
      const dateLastUpdated = Date.now();

      const prevProject = prevState.projectsById[id];
      if (!prevProject) {
        return prevState;
      }

      return {
        ...prevState,
        projectsById: {
          ...prevState.projectsById,
          [id]: {
            ...prevProject,
            dateLastUpdated,
            diagram,
            modelsById: Object.fromEntries(
              Object.entries(prevProject.modelsById).map(([modelId, model]) => [
                modelId,
                omit(model, 'draft'),
              ])
            ),
          },
        },
      };
    }

    case ProjectsActionType.REJECTED_PROJECT_DRAFT_DIAGRAM: {
      const { id, diagram } = action.payload;
      const dateLastUpdated = Date.now();

      const prevProject = prevState.projectsById[id];
      if (!prevProject) {
        return prevState;
      }

      return {
        ...prevState,
        projectsById: {
          ...prevState.projectsById,
          [id]: {
            ...prevProject,
            dateLastUpdated,
            diagram,
            modelsById: Object.fromEntries(
              Object.entries(prevProject.modelsById)
                .filter(([, model]) => !model.draft)
                .map(([modelId, model]) => [modelId, omit(model, 'draft')])
            ),
          },
        },
      };
    }

    case ProjectsActionType.PUBLISH_PROJECT_FINISHED: {
      const { id, publishHistory, intent } = action.payload;

      const prevProject = prevState.projectsById[id];
      if (!prevProject) {
        return prevState;
      }

      if (intent === Intent.SUCCESS) {
        sendHubspotEvent(TrackingEventList.DESIGNER_PUBLISH_PROJECT);
      }
      return {
        ...prevState,
        projectsById: {
          ...prevState.projectsById,
          [id]: {
            ...prevProject,
            publishHistory,
          },
        },
      };
    }

    default:
      return prevState;
  }
};
